import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wine } from 'lucide-react';

const BrandRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    // Brand Information
    brandName: '',
    contactName: '',
    email: '',
    phone: '',
    country: '',
    website: '',
    
    // Product Information
    productType: '',
    containerSize: '',
    alcoholVolume: '',
    bottlesPerCase: '',
    casesPerPallet: '',
    barcodeNumber: '',
    labelDimensions: '',
    productDescription: '',
    productImage: null,
    frontLabel: null,
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'productImage' || name === 'frontLabel') {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Append all text fields
      Object.keys(formData).forEach(key => {
        if (key !== 'productImage' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Append image file if exists
      if (formData.productImage) {
        formDataToSend.append('productImage', formData.productImage);
      }
      
      // Append front label if exists
      if (formData.frontLabel) {
        formDataToSend.append('frontLabel', formData.frontLabel);
      }

      const response = await fetch(`${API_URL}/api/brand-registration`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'Thank you! Your brand registration has been submitted. We will contact you shortly.' 
        });
        setFormData({
          brandName: '',
          contactName: '',
          email: '',
          phone: '',
          country: '',
          website: '',
          productType: '',
          containerSize: '',
          alcoholVolume: '',
          bottlesPerCase: '',
          casesPerPallet: '',
          barcodeNumber: '',
          labelDimensions: '',
          productDescription: '',
          productImage: null,
          frontLabel: null,
        });
        
        // Reset file inputs
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach(input => input.value = '');
        
        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setMessage({ type: 'error', text: data.detail || 'Registration failed. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-gray-200 p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <Wine size={48} className="text-gray-900" strokeWidth={1} />
            </div>
            <h1 className="text-4xl font-light text-gray-900 mb-4">Brand Registration</h1>
            <p className="text-lg text-gray-600 font-light">
              Register your brand for our complete logistics and clearing services
            </p>
          </div>

          {/* Success/Error Message */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Brand Information Section */}
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-6 pb-3 border-b border-gray-200">
                Brand Information
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brand Name *
                  </label>
                  <input
                    type="text"
                    name="brandName"
                    value={formData.brandName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="Your brand name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="Country of origin"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="https://yourbrand.com"
                  />
                </div>
              </div>
            </div>

            {/* Product Information Section */}
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-6 pb-3 border-b border-gray-200">
                Product Information
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type of Product *
                  </label>
                  <select
                    name="productType"
                    value={formData.productType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 bg-white"
                  >
                    <option value="">Select product type</option>
                    <option value="Wine">Wine</option>
                    <option value="Sparkling Wine">Sparkling Wine</option>
                    <option value="Spirits">Spirits</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Container Size *
                  </label>
                  <input
                    type="text"
                    name="containerSize"
                    value={formData.containerSize}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="e.g., 750ml, 1L"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alcohol Volume (%) *
                  </label>
                  <input
                    type="text"
                    name="alcoholVolume"
                    value={formData.alcoholVolume}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="e.g., 13.5%"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bottles Per Case *
                  </label>
                  <input
                    type="number"
                    name="bottlesPerCase"
                    value={formData.bottlesPerCase}
                    onChange={handleChange}
                    required
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="e.g., 12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cases Per Pallet *
                  </label>
                  <input
                    type="number"
                    name="casesPerPallet"
                    value={formData.casesPerPallet}
                    onChange={handleChange}
                    required
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="e.g., 56"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Barcode Number *
                  </label>
                  <input
                    type="text"
                    name="barcodeNumber"
                    value={formData.barcodeNumber}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="e.g., 1234567890123"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Label Dimensions *
                  </label>
                  <input
                    type="text"
                    name="labelDimensions"
                    value={formData.labelDimensions}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="e.g., 10cm x 15cm or 4in x 6in"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Front Label Image *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition">
                    <input
                      type="file"
                      name="frontLabel"
                      onChange={handleChange}
                      required
                      accept="image/*"
                      className="w-full"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Upload front label design with dimensions (JPG, PNG - Max 5MB)
                    </p>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Description
                  </label>
                  <textarea
                    name="productDescription"
                    value={formData.productDescription}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="Tell us about your product..."
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-4 bg-gray-900 text-white font-light hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Registration'}
              </button>
            </div>

            <p className="text-sm text-gray-600 text-center font-light">
              * Required fields
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BrandRegistration;
